/*---------------------------------------------------------------------------------------------
 *  Copyright (c) 2023 Vienna Hypertext Inc. All rights reserved.
 *--------------------------------------------------------------------------------------------*/

import { LinkEmbedType } from "@vienna/common/src";

import { StringUtils } from "../../../utilities/StringUtils";
export class EmbedIdentifier {
  static twitterRegex = /twitter\.com\/[^ ]+\/status\/[0-9]+[^ ]*/g;

  static youtubeRegex = /youtube\.com\/watch\?v=([^ ]+)/g;

  static shortYoutubeRegex = /youtu\.be\/([a-zA-Z0-9]+)/g;

  static youtubeEmbedRegex = /embed\/([a-zA-Z0-9]+)/g;

  static spotifyRegex =
    /(open\.spotify\.com\/((playlist)|(track)|(artist)|(album)|(show)|(episode))\/[A-Za-z0-9]{22}(\?si=[^ ]+)?)/g;

  static instagramRegex = /(instagram\.com\/p\/([^/?#&]+)).*/g;

  public static getTypeFromURL(url: URL): LinkEmbedType {
    if (this.getTweetIdFromURL(url)) return LinkEmbedType.Twitter;

    if (this.getYoutubeVideoIdFromURL(url)) return LinkEmbedType.Youtube;

    if (this.getSpotifyEmbedIdFromURL(url)) return LinkEmbedType.Spotify;

    if (this.getInstagramEmbedIdFromURL(url)) return LinkEmbedType.Instagram;

    return LinkEmbedType.Other;
  }

  /* -------------------------------------------------- */
  /*                      Twitter                       */
  /* -------------------------------------------------- */
  public static getTweetIdFromURL(url: URL): string | null {
    const simplifiedURL = this.simplifyURL(url);
    const twitterMatch = simplifiedURL.match(this.twitterRegex)?.[0];
    if (!twitterMatch || twitterMatch !== simplifiedURL) {
      return null;
    }

    const statusId = simplifiedURL.split("status/")[1];
    if (statusId) {
      const numbersOnlyRegex = /\d+/;
      const matches = statusId.match(numbersOnlyRegex);
      return matches?.[0] || null;
    } else {
      return null;
    }
  }

  /* -------------------------------------------------- */
  /*                    Youtube                    */
  /* -------------------------------------------------- */
  public static getYoutubeVideoIdFromURL(url: URL): string | null {
    const simplifiedURL = this.simplifyURL(url);
    try {
      // try mobile formatted link
      const shortMatches = simplifiedURL.match(this.shortYoutubeRegex);
      if (shortMatches && shortMatches[0] === simplifiedURL) {
        return shortMatches[0].split("youtu.be/")[1];
      }

      // try regular link
      const matches = simplifiedURL.match(this.youtubeRegex);
      if (matches && matches[0] === simplifiedURL) {
        const videoId = simplifiedURL.split("v=")[1];
        const notAmpersandRegex = /[^&]*/;
        const videoIdMatches = videoId.match(notAmpersandRegex);
        return videoIdMatches?.[0] || null;
      }

      // try embed link
      const embedMatches = simplifiedURL.match(this.youtubeEmbedRegex);
      if (embedMatches && embedMatches[0] === simplifiedURL) {
        return embedMatches[0].split("embed/")[1];
      }
      return null;
    } catch {
      return null;
    }
  }

  public static async getYoutubeTitleFromId(youtubeId: string): Promise<string> {
    const url = `https://www.youtube.com/oembed?url=https://www.youtube.com/watch?v=${youtubeId}&format=json`;
    const response = await fetch(url);
    const data = await response.json();
    return data.title;
  }

  /* -------------------------------------------------- */
  /*                      Spotify                       */
  /* -------------------------------------------------- */
  public static getSpotifyEmbedIdFromURL(url: URL): string | null {
    const simplifiedURL = this.simplifyURL(url);
    const spotifyMatch = simplifiedURL.match(this.spotifyRegex);
    if (!spotifyMatch || spotifyMatch[0] !== simplifiedURL) {
      return null;
    }
    const urlWithoutParams = StringUtils.removeAllParamsFromURLString(url.toString());
    return urlWithoutParams.split(".com/")[1] || null;
  }

  /* -------------------------------------------------- */
  /*                     Instagram                      */
  /* -------------------------------------------------- */
  public static getInstagramEmbedIdFromURL(url: URL): string | null {
    const simplifiedURL = this.simplifyURL(url);
    const instagramMatch = simplifiedURL.match(this.instagramRegex);
    if (instagramMatch && instagramMatch[0] === simplifiedURL) {
      return LinkEmbedType.Instagram;
    }
    const urlWithoutParams = StringUtils.removeAllParamsFromURLString(url.toString());
    return urlWithoutParams.split(".com/p/")[1] || null;
  }

  private static simplifyURL(url: URL): string {
    return url.toString().replace("https://", "").replace("http://", "").replace("www.", "");
  }
}
